import React, { useState, useEffect, useCallback, useRef } from 'react';
import SearchPlayers from '../../components/SearchPlayers/SearchPlayers';
import { getItemsByCountry } from '../../services/api';
import { checkPlayer } from '../../helpers/checkPlayer';
import { getImgSrc } from '../../helpers/getImageSrc';
import countryGame from '../../data/countryGame.json'; // Example: { "Albania": 3, "Germany": 5, ... }
import defaultPlayer from '../../img/defaultPlayer.jpg'; // Default image for players without an image
import './CountryGamePage.css';

// Difficulty levels and allowed ratings
const difficultyLevels = {
    "Very Easy": { allowedRatings: [5], name: "Very Easy" },
    "Easy": { allowedRatings: [4, 5], name: "Easy" },
    "Medium": { allowedRatings: [3, 4, 5], name: "Medium" },
    "Hard": { allowedRatings: [2, 3, 4, 5], name: "Hard" },
    "Very Hard": { allowedRatings: [1, 2, 3, 4, 5], name: "Legendary" },
};

// Get images using require.context
const teamImages = require.context('../../img', true, /\.jpg$/).keys().reduce((acc, key) => ({
    ...acc,
    [key.replace('./', '').replace('.jpg', '')]: require(`../../img/${key.replace('./', '')}`)
}), {});

const getTeamImage = (name) => {
    const lowerName = name.toLowerCase();
    const validKeys = Object.keys(teamImages).filter(key => {
        const baseName = key.replace('.jpg', '').toLowerCase();
        return !(baseName.includes('copa_argentina') || baseName.includes('bielorrusia'));
    });
    let key = validKeys.find(key => key.replace('.jpg', '').toLowerCase() === lowerName);
    if (!key) {
        key = validKeys.find(key => key.toLowerCase().endsWith(lowerName));
    }
    return key ? teamImages[key] : null;
};

const CountryGamePage = () => {
    // General states
    const [difficulty, setDifficulty] = useState("Medium");
    const [gameCountries, setGameCountries] = useState([]); // Array for the 10 cards
    const [currentItem, setCurrentItem] = useState(null); // { country, itemName }
    const [loadingItem, setLoadingItem] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [skipCount, setSkipCount] = useState(0); // Maximum of 5 skips
    const [message, setMessage] = useState(""); // Message after win/finish
    const [helpHighlight, setHelpHighlight] = useState(null); // Used to highlight the country card during "Help"

    // Ref to store the previous item for filtering purposes
    const previousItemRef = useRef(null);

    // Function to pick a random element from an array
    const selectRandom = (arr) => {
        if (!arr || arr.length === 0) return null;
        const idx = Math.floor(Math.random() * arr.length);
        return arr[idx];
    };

    // Load a new item from available countries (without infinite loops)
    const loadNewItem = useCallback(async (countries) => {
        const freeCountries = countries.filter(entry => !entry.player);
        if (freeCountries.length === 0) {
            setCurrentItem(null);
            setMessage("Congratulations! You have completed all 10 countries.");
            return;
        }
        const selectedCountryEntry = selectRandom(freeCountries);
        setLoadingItem(true);
        try {
            const itemsObject = await getItemsByCountry(selectedCountryEntry.country);
            const itemKeys = itemsObject ? Object.keys(itemsObject) : [];
            if (itemKeys.length === 0) {
                setErrorMsg(`No items found for ${selectedCountryEntry.country}`);
                setCurrentItem(null);
            } else {
                // Filter out the previous item if available and if there is more than one option.
                let availableItems = itemKeys;
                if (
                    previousItemRef.current &&
                    previousItemRef.current.country === selectedCountryEntry.country &&
                    itemKeys.length > 1
                ) {
                    const filteredItems = itemKeys.filter(item => item !== previousItemRef.current.itemName);
                    if (filteredItems.length > 0) {
                        availableItems = filteredItems;
                    }
                }
                const selectedItemName = selectRandom(availableItems);
                const newItem = { country: selectedCountryEntry.country, itemName: selectedItemName };
                setCurrentItem(newItem);
                // Update the ref with the new item.
                previousItemRef.current = newItem;
            }
        } catch (error) {
            console.error("Error loading item:", error);
            setErrorMsg("Error loading item.");
            setCurrentItem(null);
        }
        setLoadingItem(false);
    }, []);

    // When difficulty changes, generate a random set of 10 countries from the JSON
    useEffect(() => {
        const allowedRatings = difficultyLevels[difficulty].allowedRatings;
        const availableCountries = Object.keys(countryGame).filter(country =>
            allowedRatings.includes(countryGame[country])
        );
        let selected = [];
        if (availableCountries.length <= 10) {
            selected = availableCountries;
        } else {
            const copy = [...availableCountries];
            while (selected.length < 10) {
                const idx = Math.floor(Math.random() * copy.length);
                selected.push(copy.splice(idx, 1)[0]);
            }
        }
        const countriesForGame = selected.map(country => ({ country, player: null }));
        setGameCountries(countriesForGame);
        setCurrentItem(null);
        setErrorMsg("");
        setSkipCount(0);
        setMessage("");
        loadNewItem(countriesForGame);
    }, [difficulty, loadNewItem]);

    // Change difficulty handler
    const handleDifficultyChange = (e) => {
        setDifficulty(e.target.value);
    };

    // Handler when a player is selected from SearchPlayers
    const handlePlayerSelect = (player) => {
        setErrorMsg("");
        const playerCountry = player.pais.toLowerCase();
        const countryFound = gameCountries.find(entry =>
            entry.country.toLowerCase() === playerCountry
        );
        // Verify the player belongs to one of the game countries
        if (!countryFound) {
            setErrorMsg("The player does not belong to any of the game countries.");
            return;
        }
        // Check if the country is already occupied
        if (countryFound.player) {
            setErrorMsg("This country is already occupied by another player.");
            return;
        }
        // If the player's country is the same as the queried country,
        // force checking the item condition only.
        if (playerCountry === currentItem.country.toLowerCase()) {
            if (!checkPlayer(player, currentItem.itemName)) {
                setErrorMsg("The player does not meet the current item's requirements.");
                return;
            }
        } else {
            // Otherwise, check if the player meets either condition.
            if (!(checkPlayer(player, currentItem.itemName) || checkPlayer(player, currentItem.country))) {
                setErrorMsg("The player does not meet the current item's requirements.");
                return;
            }
        }
        // Update the game countries with the selected player.
        const newGameCountries = gameCountries.map(entry => {
            if (entry.country.toLowerCase() === playerCountry && !entry.player) {
                return { ...entry, player };
            }
            return entry;
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setGameCountries(newGameCountries);
        loadNewItem(newGameCountries);
    };

    // Skip button handler (max 5 skips)
    const handleSkip = () => {
        if (skipCount >= 5) {
            setErrorMsg("You have reached the maximum number of skips.");
            return;
        }
        setSkipCount(prev => prev + 1);
        loadNewItem(gameCountries);
    };

    // Restart handler to reset skips and remove already occupied players
    const handleRestart = () => {
        const allowedRatings = difficultyLevels[difficulty].allowedRatings;
        const availableCountries = Object.keys(countryGame).filter(country =>
            allowedRatings.includes(countryGame[country])
        );
        let selected = [];
        if (availableCountries.length <= 10) {
            selected = availableCountries;
        } else {
            const copy = [...availableCountries];
            while (selected.length < 10) {
                const idx = Math.floor(Math.random() * copy.length);
                selected.push(copy.splice(idx, 1)[0]);
            }
        }
        const countriesForGame = selected.map(country => ({ country, player: null }));
        setGameCountries(countriesForGame);
        setCurrentItem(null);
        setErrorMsg("");
        setSkipCount(0);
        setMessage("");
        loadNewItem(countriesForGame);
    };

    // Help button: highlight the country card of the current item for a few seconds
    const handleHelp = () => {
        if (currentItem) {
            setHelpHighlight(currentItem.country);
            setTimeout(() => setHelpHighlight(null), 3000);
        }
    };

    return (
        <div className="country-game">
            <div className="game-header">
                <h1 className="game-title">Footy Roster</h1>
                <div className="difficulty-selector-roster">
                    <label htmlFor="difficulty">Difficulty:</label>
                    <select id="difficulty" value={difficulty} onChange={handleDifficultyChange}>
                        {Object.keys(difficultyLevels).map(level => (
                            <option key={level} value={level}>{difficultyLevels[level].name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {message && <div className="game-message">{message}</div>}

            <div className="item-section">
                {loadingItem ? (
                    <div className="spinner"></div>
                ) : currentItem ? (
                    <div className="item-container">
                        <img
                            src={getTeamImage(currentItem.itemName)}
                            alt={currentItem.itemName}
                            className="item-image"
                        />
                    </div>
                ) : (
                    // Only show "No item available" if the game is not finished.
                    message !== "Congratulations! You have completed all 10 countries." && (
                        <div className="no-item">No item available.</div>
                    )
                )}
            </div>

            {errorMsg && <div className="error-message">{errorMsg}</div>}

            <div className="controls">
                <button
                    className="skip-button"
                    onClick={handleSkip}
                    disabled={loadingItem}
                >
                    Skip ({5 - skipCount})
                </button>
                <button
                    className="help-button"
                    onClick={handleHelp}
                    disabled={loadingItem}
                >
                    Help
                </button>
                <button
                    className="restart-button"
                    onClick={handleRestart}
                    disabled={loadingItem}
                >
                    Restart
                </button>
            </div>

            {!message && (
                <div className="search-players">
                    <SearchPlayers onSelect={handlePlayerSelect} disabled={loadingItem} />
                </div>
            )}

            <div className="countries-list">
                {gameCountries.map((entry, idx) => (
                    <div
                        key={idx}
                        className={`country-card ${helpHighlight &&
                            entry.country.toLowerCase() === helpHighlight.toLowerCase() ? "highlight" : ""
                            }`}
                    >
                        <div className="flag-box">
                            <img
                                src={getTeamImage(entry.country)}
                                alt={entry.country}
                                className="flag-image"
                            />
                        </div>
                        <div className="player-box">
                            {entry.player ? (
                                <span className="player-name-box">{entry.player.nombre}</span>
                            ) : (
                                <span className="empty-slot">Empty</span>
                            )}
                        </div>
                        <div className="avatar-box">
                            {entry.player && (
                                <img
                                    src={getImgSrc(entry.player.img)}
                                    alt={entry.player.nombre}
                                    className="player-avatar"
                                    onError={e => { e.currentTarget.src = defaultPlayer; }}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


export default CountryGamePage;
